// Multi-source intake → TaskEnvelope (#289). The intake twin of
// `handlers/github.ts`: a pure mapping layer. Every external signal (a GitHub
// issue, a Sentry alert, a Linear ticket) and every scout-authored plan (#286)
// collapses into ONE shape here — `TaskEnvelope` (`@developerz/domain`) — before
// the dispatch plane routes it to the `tasks` table and the BYOVM runners pull
// it (#121).
//
// Boundary (`apps/ingest/CLAUDE.md`): no I/O, no DB, no queue. The caller — the
// #130 Sentry route, the #131 Linear route, the scout — resolves the account +
// repo (THAT is the DB lookup) and enqueues the returned envelope. This file
// only maps fields and validates, so it stays trivially unit-testable.
//
// Two validation postures, on purpose:
//   - Raw external sources (gh/sentry/linear): defensive. Only the fields we map
//     are required; `title`/`problem` are CLAMPED to the envelope's caps —
//     untrusted text carries no length contract — and a missing body is not an
//     error. A payload that isn't even the right SHAPE is still rejected (Zod
//     throws → the route answers 400).
//   - scout-plan: strict. It is our own component's output, so a field that
//     overflows a cap is a scout BUG we want to surface, not silently truncate.
//
// Confidence, deliberately: a raw signal carries NO scoped plan, so it
// normalizes at `UNSCOPED_CONFIDENCE` (0) — below every repo
// `confidence_threshold` (default 0.6) — and the dispatcher escalates it for a
// human/scout instead of blind-dispatching a coding task ("act + log, or
// escalate + log", principles). Only a scout plan (or a user via MCP, doc 11)
// carries a real 0..1 confidence that can clear the gate and reach a runner.

import { z } from 'zod';
import {
  ERROR_SIGNATURE_FIELD_MAX,
  type ErrorSignature,
  isHttpUrl,
  RAW_TASK_URL_MAX,
  type RawTaskDraft,
  RawTaskDraftSchema,
  type RawTaskOriginRef,
  RawTaskSource,
} from '../entities/raw-task';
import type { RawTaskAuthorTrust } from '../value-objects/author-trust';
import type { AccountId } from '../value-objects/ids';
import {
  FILES_TOUCHED_MAX,
  FILES_TOUCHED_PATH_MAX,
  GROUNDING_MAX_FACTS,
  GroundedFactSchema,
  TASK_PROBLEM_MAX,
  type TaskEnvelope,
  TaskEnvelopeSchema,
  TaskOrigin,
  TaskSink,
  TaskSinkSchema,
} from '../value-objects/task-envelope';

/**
 * Resolved UPSTREAM by the ingest route (which owns the DB): which account +
 * repo this signal maps to. `repoFullName` is re-validated by
 * `TaskEnvelopeSchema` on the way out, so a bad mapping fails loud here.
 */
export type IntakeContext = {
  accountId: AccountId;
  repoFullName: string;
};

// Envelope field caps (`value-objects/task-envelope.ts`) that untrusted text is
// clamped to. `problem` is clamped to the envelope's own `TASK_PROBLEM_MAX`.
/** Mirrors `TaskEnvelopeSchema.source_ref` — clamped here so a long provider id still identifies. */
const SOURCE_REF_MAX = 200;
const TITLE_MAX = 200;

/**
 * A raw signal has no scoped plan, so it normalizes below every repo
 * `confidence_threshold` → the dispatcher escalates, never auto-dispatches. A
 * scout/user plan is the only path to a runner. Exported so the intake tests
 * (#289 QA) and the routes can assert the escalation contract.
 */
export const UNSCOPED_CONFIDENCE = 0;

/**
 * Trim + clamp to `max` (ellipsizing overflow).
 *
 * The FALLBACK goes through the same trim + cap, and that is the whole point:
 * it is untrusted external text too (`normalizeLinear` hands in the ticket's
 * `identifier`), so returning it raw broke the defensive posture this module
 * declares for raw sources in two ways at once — a 500-char identifier threw
 * `too_big` out of `TaskEnvelopeSchema` for a field the mapper had promised to
 * clamp, and a whitespace-only one produced an envelope whose `title` was
 * literally three spaces. `last` is the module's own literal, so the result is
 * non-empty by construction.
 */
function clamp(text: string, max: number, fallback: string, last = 'untitled'): string {
  for (const candidate of [text, fallback, last]) {
    const trimmed = candidate.trim();
    if (trimmed.length === 0) continue;
    return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1)}…`;
  }
  return last;
}

/** Append a provenance line so the runner can trace the signal back to source. */
function withSource(body: string, url: string | null | undefined): string {
  return url ? `${body}\n\nSource: ${url}` : body;
}

// ─── raw external sources (defensive: strict shape, clamped length) ────────────

/** Slice of a GitHub `issues` webhook payload we map. */
const GhIssueSourceSchema = z.object({
  issue: z.object({
    /**
     * The issue's number — the durable task↔issue link the lifecycle narration
     * comments on (#366). `nullish` like the other mapped fields: this mapper is
     * DEFENSIVE about raw external payloads, and an issue we can't number simply
     * narrates nowhere rather than failing the whole intake.
     */
    number: z.number().int().positive().nullish(),
    title: z.string(),
    body: z.string().nullish(),
    html_url: z.string().nullish(),
  }),
});

/** One `stacktrace.frames[]` entry of a Sentry event: the fields the ask renders. */
const SentryFrameSchema = z.object({
  filename: z.string().nullish(),
  lineno: z.number().nullish(),
  function: z.string().nullish(),
  in_app: z.boolean().nullish(),
});

/**
 * Slice of a Sentry webhook payload. Covers both the `issue` resource (new-issue
 * alerts) and the `event_alert` shape (`data.event`), preferring the issue.
 */
const SentrySourceSchema = z.object({
  data: z.object({
    issue: z
      .object({
        title: z.string(),
        culprit: z.string().nullish(),
        shortId: z.string().nullish(),
        web_url: z.string().nullish(),
        permalink: z.string().nullish(),
        /**
         * The group's exception summary (#6223): read only when the event carries no
         * exception values. `metadata.value` (the error MESSAGE) is deliberately not
         * in this schema, so zod strips it and nothing downstream can read it. Every
         * signature field `.catch(null)`es: a wrong wire type there must not turn a
         * delivery that filed before #6223 into a permanent 400.
         */
        metadata: z
          .object({
            type: z.string().nullish().catch(null),
            filename: z.string().nullish().catch(null),
            function: z.string().nullish().catch(null),
          })
          .nullish()
          .catch(null),
      })
      .nullish(),
    event: z
      .object({
        title: z.string(),
        web_url: z.string().nullish(),
        /**
         * The GROUP this occurrence belongs to. An `event_alert` body carries no
         * `data.issue` at all, so without this field the only ids on it name ONE
         * occurrence — and an inlet that keys on those mints an ask per alert.
         * Sentry sends it as a string, but a number is the ordinary drift for a
         * numeric id crossing a JSON boundary and this mapper is DEFENSIVE about
         * raw external payloads: refusing the whole body over the wire type would
         * fail an intake we can read.
         */
        issue_id: z.union([z.string(), z.number()]).nullish(),
        release: z.string().nullish(),
        /**
         * Every level is `nullish`: a throw here is a permanent `400 invalid_payload`
         * at the ingest route, and a delivery without a trace is still an ask.
         */
        exception: z
          .object({
            values: z
              .array(
                z.object({
                  /** The exception CLASS (`TypeError`), never its message (#6223). */
                  type: z.string().nullish().catch(null),
                  stacktrace: z.object({ frames: z.array(SentryFrameSchema).nullish() }).nullish(),
                }),
              )
              .nullish(),
          })
          .nullish(),
      })
      .nullish(),
  }),
});

/** Slice of a Linear webhook payload (`type: 'Issue'`). */
const LinearSourceSchema = z.object({
  data: z.object({
    title: z.string(),
    description: z.string().nullish(),
    identifier: z.string().nullish(),
    url: z.string().nullish(),
  }),
});

/**
 * A scout-authored plan (#286) — strict, because it is our own output. The
 * optional rich fields default to empty so the scout can author a minimal plan;
 * `sink` defaults to `direct` (the only sink that dispatches a coding task).
 */
const ScoutPlanSchema = z.object({
  title: z.string().min(1).max(TITLE_MAX),
  problem: z.string().min(1).max(TASK_PROBLEM_MAX),
  acceptance_criteria: z.array(z.string().min(1).max(500)).max(20).default([]),
  /**
   * Bounded by the ENVELOPE's own constants, not by a second copy of the two
   * numbers (#2299). This is the mint the scout's declared blast radius passes
   * through — the box already ran it past `provableFilesTouched`, whose whole
   * contract is that it enforces the same caps `TaskEnvelopeSchema` rejects on.
   * A literal here is a third spelling of one cap, and the failure it causes is
   * silent in the worst direction: the box degrades a set "safely" to something
   * it believes fits, and this parse refuses it — costing the enqueue, not the
   * declaration. Since #2278 that set is also the only thing letting two boxes
   * onto one repo, so losing it here loses the concurrency it was minted for.
   */
  files_touched: z
    .array(z.string().min(1).max(FILES_TOUCHED_PATH_MAX))
    .max(FILES_TOUCHED_MAX)
    .default([]),
  forbidden: z.array(z.string().min(1).max(300)).max(50).default([]),
  confidence: z.number().min(0).max(1),
  sink: TaskSinkSchema.default(TaskSink.Direct),
  /**
   * The survey's anchored facts (`TaskEnvelopeSchema.grounding`). Bounded by the
   * ENVELOPE's own schema for exactly the reason `files_touched` above is: this is
   * the mint the box's grounding passes through, and a second copy of the caps here
   * would refuse at the parse what `boundedGrounding` already trimmed to fit.
   *
   * `.optional()` and not `.default([])` — an absent field must stay absent through
   * the rebuild below, so a lane that carries no grounding mints the same envelope
   * it minted before rather than one with an empty array bolted on.
   */
  grounding: z.array(GroundedFactSchema).max(GROUNDING_MAX_FACTS).optional(),
});

// ─── mappers ───────────────────────────────────────────────────────────────

/**
 * Build a raw-source envelope: no scoped-plan fields, `UNSCOPED_CONFIDENCE`,
 * `direct` sink, `system` provenance. Final `TaskEnvelopeSchema.parse` is the
 * single validation gate (repo-name regex, account-id brand, field caps).
 */
function buildRawEnvelope(input: {
  origin: TaskEnvelope['origin'];
  source: string;
  ctx: IntakeContext;
  title: string;
  problem: string;
  /** The GitHub issue this signal came from (#366) — omitted by every non-gh source. */
  issueNumber?: number | undefined;
  /**
   * The provider's stable id for the THING, not for this delivery — see
   * `TaskEnvelopeSchema.source_ref`. Clamped rather than refused: an over-long ref
   * still identifies, and losing idempotency over a long string would be the worse
   * trade.
   */
  sourceRef?: string | undefined;
  /**
   * The provider's PERMALINK for that same thing (#3453) — see
   * `TaskEnvelopeSchema.source_url`. FILTERED, never refused: a source that sends
   * no link, or sends something `isHttpUrl` does not recognise, still files its
   * ask. Same asymmetry `urlEvidence` takes, and for the same reason.
   */
  sourceUrl?: string | null | undefined;
}): TaskEnvelope {
  return TaskEnvelopeSchema.parse({
    origin: input.origin,
    account_id: input.ctx.accountId,
    repo_full_name: input.ctx.repoFullName,
    title: input.title,
    problem: input.problem,
    acceptance_criteria: [],
    files_touched: [],
    forbidden: [],
    confidence: UNSCOPED_CONFIDENCE,
    sink: TaskSink.Direct,
    dispatched_by: { kind: 'system', source: input.source },
    ...(input.issueNumber !== undefined ? { issue_number: input.issueNumber } : {}),
    ...(input.sourceRef !== undefined && input.sourceRef.trim() !== ''
      ? { source_ref: input.sourceRef.trim().slice(0, SOURCE_REF_MAX) }
      : {}),
    ...(typeof input.sourceUrl === 'string' &&
    input.sourceUrl.length <= RAW_TASK_URL_MAX &&
    isHttpUrl(input.sourceUrl)
      ? { source_url: input.sourceUrl }
      : {}),
  });
}

/** GitHub issue → `TaskEnvelope{origin:'gh_issue'}`. */
export function normalizeGhIssue(payload: unknown, ctx: IntakeContext): TaskEnvelope {
  const { issue } = GhIssueSourceSchema.parse(payload);
  const title = clamp(issue.title, TITLE_MAX, 'GitHub issue');
  const problem = clamp(withSource(issue.body ?? '', issue.html_url), TASK_PROBLEM_MAX, title);
  return buildRawEnvelope({
    origin: TaskOrigin.GhIssue,
    source: 'gh_issue',
    ctx,
    title,
    problem,
    // The narration's thread before a PR exists (#366). Nullish ⇒ omitted, never
    // guessed from the `html_url` tail: a link we can't read is not a link.
    ...(issue.number != null ? { issueNumber: issue.number } : {}),
    // The same url the prose already carries, as DATA (#3453). This lane passes no
    // `sourceRef` at all, so before this its asks reached triage with an empty
    // `evidence.urls` — and it is 313 of prod's 333 raw tasks.
    sourceUrl: issue.html_url,
  });
}

/**
 * A Sentry event alert's GROUP id as a readable ref, or nothing.
 *
 * Empty and whitespace are absent, not a ref: `sentry-issue:` is non-empty, so
 * composing it unguarded would hand every id-less alert on the platform the SAME
 * stable-looking id — and an inlet keyed on it would fold unrelated errors into
 * one ask while reporting perfect idempotency.
 */
function sentryGroupRef(issueId: string | number | null | undefined): string | undefined {
  if (issueId == null) return undefined;
  const id = String(issueId).trim();
  return id === '' ? undefined : `sentry-issue:${id}`;
}

const SENTRY_TRACE_FRAMES_MAX = 15;
const SENTRY_FRAME_LINE_MAX = 200;

/** One frame as `file:line function`, with whatever parts it has; empty when it has none. */
function sentryFrameLine(frame: z.infer<typeof SentryFrameSchema>): string {
  const location = [frame.filename, frame.lineno]
    .filter((part) => part != null && part !== '')
    .join(':');
  const line = [location, frame.function ?? ''].filter((part) => part !== '').join(' ');
  return line.trim() === '' ? '' : clamp(line, SENTRY_FRAME_LINE_MAX, '');
}

/**
 * The release and the crash-site frames of the LAST exception value, as the
 * lines that follow the `Source:` line. Sentry orders `frames` oldest first, the
 * crash site last (`parseStackFrames`, `packages/observability`), so the kept
 * frames are the END of the array, reversed, with in-app frames grouped first.
 */
function sentryEventDetail(event: z.infer<typeof SentrySourceSchema>['data']['event']): string {
  const values = event?.exception?.values ?? [];
  const frames = (values[values.length - 1]?.stacktrace?.frames ?? [])
    .map((frame) => ({ inApp: frame.in_app === true, line: sentryFrameLine(frame) }))
    .filter((frame) => frame.line !== '')
    .slice(-SENTRY_TRACE_FRAMES_MAX)
    .reverse();
  const ordered = [...frames.filter((f) => f.inApp), ...frames.filter((f) => !f.inApp)];
  const sections = [
    event?.release ? `Release: ${event.release}` : null,
    ordered.length > 0
      ? ['stacktrace (in-app first, crash site first):', ...ordered.map((f) => f.line)].join('\n')
      : null,
  ].filter((section): section is string => section !== null);
  return sections.map((section) => `\n\n${section}`).join('');
}

type SentryData = z.infer<typeof SentrySourceSchema>['data'];

/**
 * Trim + hard-cut to the signature cap; blank is absent. Untrusted text is clamped, never
 * refused, and with no marker: a signature field is matched on, not read. A cut landing
 * inside a surrogate pair drops the half — a lone surrogate is refused by `jsonb`.
 */
function signatureText(text: string | null | undefined): string | null {
  const trimmed = text?.trim() ?? '';
  if (trimmed === '') return null;
  const cut = trimmed.slice(0, ERROR_SIGNATURE_FIELD_MAX);
  const whole = /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
  return whole === '' ? null : whole;
}

/**
 * The delivery's {@link ErrorSignature}, or null when it names no group (it files
 * nothing then: `fileVendorAsk` drops it as `no_stable_id`).
 *
 * The LAST exception value is the one raised (Sentry chains causes first), and its
 * crash site is the last in-app frame, else the last frame (frames run oldest first).
 * `frame_in_app` records which of the two the stored crash frame is (#5653); the
 * `issue.metadata` fallback has no frame and stores `false`.
 * `issue.metadata` is the fallback only for an event with no exception values: the
 * `issue` resource carries no event at all. No message field is read anywhere here.
 */
function sentryErrorSignature(
  data: SentryData,
  groupId: string | undefined,
): ErrorSignature | null {
  if (groupId === undefined) return null;
  const values = data.event?.exception?.values ?? [];
  const raised = values[values.length - 1];
  if (raised === undefined) {
    const metadata = data.issue?.metadata;
    return {
      exception_class: signatureText(metadata?.type),
      file: signatureText(metadata?.filename),
      function: signatureText(metadata?.function),
      group_id: groupId,
      frame_in_app: false,
    };
  }
  const frames = raised.stacktrace?.frames ?? [];
  const inAppCrashSite = frames.findLast((frame) => frame.in_app === true);
  const crashSite = inAppCrashSite ?? frames[frames.length - 1];
  return {
    exception_class: signatureText(raised.type),
    file: signatureText(crashSite?.filename),
    function: signatureText(crashSite?.function),
    group_id: groupId,
    frame_in_app: inAppCrashSite !== undefined,
  };
}

/** A Sentry delivery, parsed once: the envelope and the signature (#6223), from one parse. */
export type SentryIntake = { envelope: TaskEnvelope; errorSignature: ErrorSignature | null };

/**
 * Sentry alert → its envelope AND its {@link ErrorSignature}, from ONE
 * `SentrySourceSchema.parse` ("one parse, two projections"). `group_id` is the
 * envelope's own `source_ref`, never a second walk of the ref ladder.
 */
export function normalizeSentryIntake(payload: unknown, ctx: IntakeContext): SentryIntake {
  const { data } = SentrySourceSchema.parse(payload);
  const envelope = sentryEnvelope(data, ctx);
  return { envelope, errorSignature: sentryErrorSignature(data, envelope.source_ref) };
}

/** Sentry alert → `TaskEnvelope{origin:'sentry'}`. */
export function normalizeSentry(payload: unknown, ctx: IntakeContext): TaskEnvelope {
  return normalizeSentryIntake(payload, ctx).envelope;
}

function sentryEnvelope(data: SentryData, ctx: IntakeContext): TaskEnvelope {
  const issue = data.issue ?? null;
  const event = data.event ?? null;
  const rawTitle = issue?.title ?? event?.title;
  if (rawTitle === undefined) {
    throw new z.ZodError([
      { code: 'custom', path: ['data'], message: 'sentry payload has neither issue nor event' },
    ]);
  }
  const title = clamp(rawTitle, TITLE_MAX, 'Sentry issue');
  const body = [issue?.culprit, issue?.shortId ? `Sentry ${issue.shortId}` : null]
    .filter((part): part is string => Boolean(part))
    .join('\n');
  const url = issue?.web_url ?? issue?.permalink ?? event?.web_url;
  // The trace goes AFTER the `Source:` line so the clamp trims it first.
  const problem = clamp(
    `${withSource(body, url)}${sentryEventDetail(event)}`,
    TASK_PROBLEM_MAX,
    title,
  );
  // `shortId` first (`PROJ-1AB` — stable for the life of the issue and readable in
  // an audit row), then the permalink, which is stable for the same reason and is
  // present when `shortId` is not. The EVENT url is deliberately not a candidate:
  // it identifies one occurrence, so keying on it would make every occurrence of
  // one error its own task — the opposite of what this is for.
  //
  // The event's `issue_id` is the last rung and is a GROUP id, not an occurrence
  // id, which is what makes it admissible where `event.web_url` is not. It is
  // prefixed because the rungs above it are short ids and urls: a bare `4821521`
  // in an audit row names nothing a reader can act on. Without this rung an
  // `event_alert` body — Sentry's other half — carries no stable id at all, and
  // an inlet that keys asks on this field would have to drop every one of them.
  const sourceRef =
    issue?.shortId ?? issue?.permalink ?? issue?.web_url ?? sentryGroupRef(event?.issue_id);
  return buildRawEnvelope({
    origin: TaskOrigin.Sentry,
    source: 'sentry',
    ctx,
    title,
    problem,
    ...(sourceRef ? { sourceRef } : {}),
    // The permalink `withSource` already spent on the prose, carried as DATA too
    // (#3453). `sourceRef` above is the issue SHORT ID whenever Sentry sends one,
    // which is not a link — so without this the field that exists to be followed
    // was empty for exactly the payloads that carry a link.
    sourceUrl: url,
  });
}

/** Linear ticket → `TaskEnvelope{origin:'linear'}`. */
export function normalizeLinear(payload: unknown, ctx: IntakeContext): TaskEnvelope {
  const { data } = LinearSourceSchema.parse(payload);
  // `identifier` is external text with no length or emptiness contract, so it is
  // a CANDIDATE, not the guaranteed answer — `'Linear ticket'` is the rung below it.
  const title = clamp(data.title, TITLE_MAX, data.identifier ?? '', 'Linear ticket');
  const problem = clamp(withSource(data.description ?? '', data.url), TASK_PROBLEM_MAX, title);
  // `identifier` is the ticket key (`ENG-123`) and is what a human would call it;
  // the url is the fallback for a payload that omits it. Both name the TICKET, not
  // the update — a Linear webhook fires on every edit, and keying on the delivery
  // would mint a task per comment.
  const sourceRef = data.identifier ?? data.url ?? undefined;
  return buildRawEnvelope({
    origin: TaskOrigin.Linear,
    source: 'linear',
    ctx,
    title,
    problem,
    ...(sourceRef ? { sourceRef } : {}),
    // Sentry's rule, same shape (#3453): `identifier` (`ENG-123`) is the ref a
    // human would use and is not a link, so the ticket url rides its own field.
    sourceUrl: data.url,
  });
}

/** Scout-authored plan (#286) → `TaskEnvelope{origin:'manual'}`. Strict. */
export function normalizeScoutPlan(payload: unknown, ctx: IntakeContext): TaskEnvelope {
  const plan = ScoutPlanSchema.parse(payload);
  return TaskEnvelopeSchema.parse({
    origin: TaskOrigin.Manual,
    account_id: ctx.accountId,
    repo_full_name: ctx.repoFullName,
    title: plan.title,
    problem: plan.problem,
    acceptance_criteria: plan.acceptance_criteria,
    files_touched: plan.files_touched,
    forbidden: plan.forbidden,
    confidence: plan.confidence,
    sink: plan.sink,
    // THE RE-NORMALIZE PATH IS WHERE THIS WOULD HAVE DIED. This function REBUILDS
    // the envelope from the enumerated list above rather than spreading the payload
    // (see `apps/worker/src/fleet/scout-plan-ingest.ts`, which says so about
    // `require_approval`), so a field the box publishes but this list omits is
    // dropped by the very re-validation meant to preserve it — silently, with a
    // green parse on both sides.
    ...(plan.grounding !== undefined && plan.grounding.length > 0
      ? { grounding: plan.grounding }
      : {}),
    dispatched_by: { kind: 'system', source: 'scout' },
  });
}

// ─── unified entry point ─────────────────────────────────────────────────────

/** A signal to normalize. `payload` is the raw source body (parsed defensively). */
export type IntakeSource =
  | { kind: 'gh_issue'; payload: unknown }
  | { kind: 'sentry'; payload: unknown }
  | { kind: 'linear'; payload: unknown }
  | { kind: 'scout_plan'; payload: unknown };

/**
 * The one intake surface: map any source to a validated `TaskEnvelope`. Throws
 * `ZodError` on a malformed payload — the caller maps it (route → 400). Add a
 * new source in exactly one place: the union above + a `case` here.
 */
export function normalizeIntake(source: IntakeSource, ctx: IntakeContext): TaskEnvelope {
  switch (source.kind) {
    case 'gh_issue':
      return normalizeGhIssue(source.payload, ctx);
    case 'sentry':
      return normalizeSentry(source.payload, ctx);
    case 'linear':
      return normalizeLinear(source.payload, ctx);
    case 'scout_plan':
      return normalizeScoutPlan(source.payload, ctx);
    default: {
      const exhaustive: never = source;
      throw new Error(`unknown intake source: ${JSON.stringify(exhaustive)}`);
    }
  }
}

// ─── the raw-task twin (arc 101 doc 01, #2311) ───────────────────────────────
//
// Everything above answers a `TaskEnvelope` — EXECUTABLE work, the right-hand
// column of `docs/idea/lifecycle.md` § "The spine". What follows answers the
// left-hand column: a `RawTaskDraft`, the ask before anything has decided
// whether or how it should happen.
//
// It is built FROM the envelope rather than from the payload a second time, and
// that is deliberate. A second per-source parser would be a second place for a
// Sentry field to be read, a second set of caps, and a second thing to keep in
// step — the exact drift `normalizeIntake` was introduced to end (#289). One
// parse, two projections.
//
// WHAT IS DROPPED ON THE WAY, and why each is right: `confidence` (a raw task
// has none — confidence is triage's output, arrived at against a board this
// object has not seen), `sink`, `forbidden`, `class` (scheduling), and
// `dispatched_by` (the row's `origin_ref` names what filed it). `files_touched`
// survives, but as `evidence.anchors` — read as the scout's CITATIONS, which triage
// narrows into a declared blast radius, never as a declared blast radius itself
// (`docs/idea/triage.md`: "the scout's anchors, narrowed to what this task will
// touch").
//
// `grounding` is the envelope's REAL citation field and is deliberately NOT read
// here yet. Nothing calls this projection today — `handleRawTaskBridge` mints no
// task and the scout lane files no `raw_tasks` row (#2325) — so wiring the better
// source would be a change no producer exercises. Whoever closes #2325 should
// prefer it: it carries `path:line` where `files_touched` carries only `path`.

/**
 * The inlet-specific half a raw task cannot derive from the envelope: what filed
 * it, and the key that makes re-filing it a no-op.
 *
 * `dedupeKey` is the whole idempotency contract, and it is the CALLER's because
 * only the inlet knows its source's stable id for the THING — a scout run plus a
 * plan-local id, a Sentry short id, an issue number. Keyed on a DELIVERY instead
 * and a webhook that fires on every edit files a raw task per comment.
 */
export type RawTaskRef = {
  originRef: RawTaskOriginRef;
  dedupeKey: string;
  /**
   * Keys / MCP servers / secrets this work needs — the scout's one
   * implementation-adjacent finding (`docs/idea/triage.md`). Absent ⇒ none
   * named, which is not the same as "none needed"; nothing infers it.
   */
  requiredAccess?: readonly string[] | undefined;
  /**
   * Who wrote the text this ask carries, stamped by the door (`raw_tasks.author_trust`).
   * REQUIRED, no default: the envelope does not know its author, only the door does,
   * so a door that forgets it fails to compile rather than filing an unstamped ask.
   */
  authorTrust: RawTaskAuthorTrust;
  /** The error delivery's signature (#6223). Absent for every inlet but Sentry. */
  errorSignature?: ErrorSignature | undefined;
};

/**
 * A GitHub issue's ask key, for ONE generation of that ask: GitHub's own GLOBAL
 * issue id, namespaced by inlet.
 *
 * ONE FUNCTION spells every generation (#5665). Generation 1 — the default, and
 * what every door that files an issue's FIRST ask passes (the `issues.opened`
 * intake, `dispatch_fleet`'s file-the-ask, the scout backlog drain, the backlog
 * import) — is `gh_issue:<id>`, byte-identical to the key before generations
 * existed, so no stored row changes. A person reopening an issue whose newest ask
 * settled files generation N >= 2 as `gh_issue:<id>:<N>`
 * (`apps/worker/src/dispatch/gh-issue-intake.ts#ghIssueReopenFiling`); `:1` is
 * never written, so the base key and `findAnchorHead`'s generation 0 are the same
 * row. Hand-spelling either form would let the same generation file twice, which is
 * the double-mint this key exists to make impossible. It is a STORED value
 * (`raw_tasks.dedupe_key`, unique per `(account_id, dedupe_key)`), so changing it
 * re-files every issue already judged.
 *
 * The id and not `owner/repo#number`: a repo rename or an issue transfer changes
 * both halves of the readable form, so a key built from it files a SECOND ask for
 * an issue already judged. `issue.id` is assigned once and never moves. The
 * readable form rides beside it in `origin_ref` — see {@link ghIssueOriginId}.
 *
 * THROWS on a `reopenGeneration` that is not an integer >= 1: a fractional or
 * zero generation is a caller's arithmetic bug, and a key spelled from it would be
 * stored forever.
 */
export function ghIssueDedupeKey(issueId: number, reopenGeneration = 1): string {
  if (!Number.isInteger(reopenGeneration) || reopenGeneration < 1) {
    throw new Error(
      `ghIssueDedupeKey: reopenGeneration must be an integer >= 1 (1 is the base key ` +
        `gh_issue:${issueId}), got ${reopenGeneration}`,
    );
  }
  return reopenGeneration === 1 ? `gh_issue:${issueId}` : `gh_issue:${issueId}:${reopenGeneration}`;
}

/**
 * Which filing of its issue a {@link ghIssueDedupeKey} names: N >= 2 on a later generation, `null`
 * on the base key or any other key. A machine fact, read by triage for its `filed again:` line.
 */
export function ghIssueFilingOfKey(dedupeKey: string): number | null {
  const m = /^gh_issue:\d+:(\d+)$/.exec(dedupeKey);
  return m === null ? null : Number(m[1]);
}

/** `owner/repo#number` — the `origin_ref.id` a human reading a triage batch recognises. */
export function ghIssueOriginId(repoFullName: string, issueNumber: number): string {
  return `${repoFullName}#${issueNumber}`;
}

/** Which INLET a normalized source is, as the raw task records it. Total — no default. */
function rawTaskSourceOf(kind: IntakeSource['kind']): RawTaskSource {
  switch (kind) {
    case 'gh_issue':
      return RawTaskSource.GhIssue;
    case 'sentry':
      return RawTaskSource.Sentry;
    case 'linear':
      return RawTaskSource.Linear;
    case 'scout_plan':
      return RawTaskSource.Scout;
  }
}

/**
 * A `source_ref` as followable evidence, or nothing.
 *
 * FILTERS rather than refuses, and the asymmetry with the schema is the point: a
 * `source_ref` is usually a short id (`PROJ-1AB`), so "not a link" is the
 * ordinary case and must cost the ask nothing, while a hand-built draft carrying
 * a broken url is a bug the schema refuses. Both sides ask `isHttpUrl`, so they
 * cannot disagree about what a link is — the regex that stood here accepted
 * `http://`, which has no host at all.
 */
function urlEvidence(...candidates: readonly (string | undefined)[]): string[] {
  for (const candidate of candidates) {
    if (candidate === undefined || candidate.length > RAW_TASK_URL_MAX) continue;
    if (isHttpUrl(candidate)) return [candidate];
  }
  return [];
}

/**
 * Project a validated `TaskEnvelope` onto the ask it came from.
 *
 * Exported because the scout lane already holds a normalized envelope
 * (`apps/worker/src/fleet/scout-plan-ingest.ts` normalizes, then stamps the
 * conversation ref) and re-parsing the payload to reach the same fields would be
 * two parses of one payload with two chances to disagree.
 *
 * `RawTaskDraftSchema.parse` on the way out is the single validation gate, so an
 * envelope whose anchors or ask overflow the raw task's own caps fails HERE,
 * loudly, rather than at the insert.
 */
export function rawTaskFromEnvelope(
  envelope: TaskEnvelope,
  source: RawTaskSource,
  ref: RawTaskRef,
): RawTaskDraft {
  // The intent in prose: what should be true. Acceptance criteria are part of
  // that ("done means…"), not part of how to do it, so they ride the ask rather
  // than being dropped — a triage agent reading only the title and problem would
  // re-derive them worse.
  const criteria =
    envelope.acceptance_criteria.length > 0
      ? `\n\nAcceptance criteria:\n${envelope.acceptance_criteria.map((c) => `- ${c}`).join('\n')}`
      : '';
  return RawTaskDraftSchema.parse({
    accountId: envelope.account_id,
    source,
    // Singular here and PLURAL on the row: every envelope names exactly one repo
    // (an executable task cannot span trees), but a raw task filed by a devops
    // run or a cross-repo ask can, so the column holds the general case from the
    // day it exists rather than being widened later. The architect origin is the
    // one repo-less exception (#2683) — it normalizes to an empty `repoFullNames`
    // array, which the column already accepts (`repoFullNames.max(RAW_TASK_REPOS_MAX)`
    // is the cap, the floor is 0).
    repoFullNames: envelope.repo_full_name === undefined ? [] : [envelope.repo_full_name],
    // COMPOSED WHOLE, never clamped. `clamp` stood here and it was the front-door
    // spelling of the defect this arc spent #2310 on: a valid envelope can carry
    // a 10,000-character problem and twenty acceptance criteria, and shortening
    // that at admission hands triage a different ask than the scout wrote —
    // silently, and the tail it drops is usually where the constraints are. The
    // cap is derived to sit above anything a valid envelope can compose to
    // (`RAW_TASK_ASK_MAX`), so this is total for real input and the parse below
    // is a fail-fast gate on a hand-built draft rather than a shaper of one.
    ask: `${envelope.title}\n\n${envelope.problem}${criteria}`,
    evidence: {
      anchors: envelope.files_touched.map((path) => ({ path })),
      // `source_url` FIRST, `source_ref` behind it (#3453). The fallback is not
      // legacy tolerance: a Sentry payload with no short id already puts the
      // permalink in `source_ref` (it is that field's third rung), so dropping the
      // fallback would lose the link for exactly the sources that have only one
      // way to say it.
      //
      // ORDERED CANDIDATES, never `a ?? b`: the coalesce picks the first field
      // that is PRESENT, so a present-but-unfollowable `source_url` would shadow a
      // real link in `source_ref` and answer []. `buildRawEnvelope` cannot produce
      // that pair — it filters on the way in — but `TaskEnvelopeSchema` is a wire
      // contract that bounds this field's LENGTH and not its shape, and
      // `rawTaskFromEnvelope` is exported for producers that build an envelope by
      // hand. So the filter picks, rather than the presence check.
      urls: urlEvidence(envelope.source_url, envelope.source_ref),
      excerpts: [],
    },
    requiredAccess: [...(ref.requiredAccess ?? [])],
    originRef: ref.originRef,
    dedupeKey: ref.dedupeKey,
    authorTrust: ref.authorTrust,
    ...(ref.errorSignature !== undefined ? { errorSignature: ref.errorSignature } : {}),
  });
}

/**
 * The one raw-task intake surface: map any source to a validated `RawTaskDraft`.
 * Throws `ZodError` on a malformed payload, exactly as `normalizeIntake` does —
 * it IS `normalizeIntake`, projected.
 *
 * Adding a source stays a one-place change: the `IntakeSource` union above, plus
 * a `case` in `normalizeIntake` and in `rawTaskSourceOf`. Both switches are
 * total with no default, so neither can be forgotten.
 */
export function normalizeRawIntake(
  source: IntakeSource,
  ctx: IntakeContext,
  ref: RawTaskRef,
): RawTaskDraft {
  return rawTaskFromEnvelope(normalizeIntake(source, ctx), rawTaskSourceOf(source.kind), ref);
}
